import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Label } from '../../../components/ui/label';
import { FormInput } from '../../../components/ui/form-input';
import { FormTextarea } from '../../../components/ui/form-textarea';

interface CustomFAQ {
  id: string;
  question: string;
  answer: string;
  required: boolean;
}

interface CustomFAQsTabContentProps {
  customFAQs: CustomFAQ;
  id: string;
  index: number;
  handleQuestionChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAnswerChange: (value: string, index: number) => void;
  removeCustomFAQs: (index: number) => void;
  handleFieldFocus: (fieldName: string) => void;
  isFieldInvalid: (fieldName: string) => boolean;
}

function CustomFAQsTabContent(props: CustomFAQsTabContentProps) {
  const {
    customFAQs,
    id,
    index,
    handleQuestionChange,
    handleAnswerChange,
    removeCustomFAQs,
    handleFieldFocus,
    isFieldInvalid,
  } = props;

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          {...provided.draggableProps}
          ref={provided.innerRef}
          className="space-y-2 rounded-md border border-edge bg-paper-inset p-2"
        >
          {/* Question Field with drag/delete icons */}
          <div className="space-y-1">
            <div className="flex items-start justify-between">
              <Label className="text-base font-medium text-ink-secondary">Question</Label>
              <div className="flex items-center gap-1">
                <div {...provided.dragHandleProps} className="cursor-move">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M2 4H14M2 8H14M2 12H14"
                      stroke="#3A3429"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <button onClick={() => removeCustomFAQs(index)} className="hover:opacity-70">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M4 4L12 12M4 12L12 4"
                      stroke="#3A3429"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <FormInput
              id={`faqs-faq-question-${index}`}
              data-type="question"
              data-index={index}
              value={customFAQs.question}
              onChange={handleQuestionChange}
              onFocus={() => handleFieldFocus(`faqs-faq-question-${index}`)}
              maxLength={90}
              className={`h-7 ${
                isFieldInvalid(`faqs-faq-question-${index}`) ? 'border-red-500' : ''
              }`}
            />
            <p className={`text-xs text-right ${
              customFAQs.question.length > 80 ? 'text-red-500' : 'text-ink-muted'
            }`}>
              {customFAQs.question.length}/90
            </p>
          </div>

          {/* Answer Field */}
          <div className="space-y-1">
            <Label className="text-base font-medium text-ink-secondary">Answer</Label>
            <FormTextarea
              id={`answer-${index}`}
              data-type="answer"
              data-index={index}
              value={customFAQs.answer}
              onChange={(e) => handleAnswerChange(e.target.value, index)}
              onFocus={() => handleFieldFocus(`faqs-faq-answer-${index}`)}
              rows={5}
              maxLength={280}
              className={`resize-none ${
                isFieldInvalid(`faqs-faq-answer-${index}`) ? 'border-red-500' : ''
              }`}
            />
            <p className={`text-xs text-right ${
              customFAQs.answer.length > 260 ? 'text-red-500' : 'text-ink-muted'
            }`}>
              {customFAQs.answer.length}/280
            </p>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default CustomFAQsTabContent;
